/**
 * Topology API - Real agent data endpoints
 * Connects UI visualizations to actual EquilateralAgents system data
 */

export interface AgentNode {
  id: string;
  name: string;
  capabilities: string[];
  category: string;
}

export interface AgentEdge {
  from: string;
  to: string;
  type: 'depends_on' | 'triggers' | 'observed';
  label: string;
  weight?: number;
}

export interface Topology {
  namespace: string;
  version: string;
  type: 'workflow' | 'observed';
  nodes: AgentNode[];
  edges: AgentEdge[];
}

// Real agents from ~/Source/EquilateralAgents/src/agents/specialists/
const REAL_AGENTS: AgentNode[] = [
  {
    id: 'SecurityReviewerAgent',
    name: 'Security Reviewer',
    capabilities: ['security_analysis', 'vulnerability_scan', 'compliance_check'],
    category: 'security'
  },
  {
    id: 'AuditorAgent',
    name: 'Auditor',
    capabilities: ['standards_compliance', 'code_review', 'pattern_validation'],
    category: 'validation'
  },
  {
    id: 'PatternHarvestingAgent',
    name: 'Pattern Harvester',
    capabilities: ['pattern_extraction', 'code_analysis', 'knowledge_synthesis'],
    category: 'analysis'
  },
  {
    id: 'DeploymentAgent',
    name: 'Deployment',
    capabilities: ['sam_deploy', 'cloudformation', 'environment_setup'],
    category: 'infrastructure'
  },
  {
    id: 'TestAgent',
    name: 'Test',
    capabilities: ['ui_testing', 'element_remapping', 'playwright_execution'],
    category: 'testing'
  },
  {
    id: 'DatabaseAgent',
    name: 'Database',
    capabilities: ['query_execution', 'schema_management', 'data_migration'],
    category: 'data'
  },
  {
    id: 'AgentClassifier',
    name: 'Agent Classifier',
    capabilities: ['task_classification', 'agent_routing', 'capability_matching'],
    category: 'orchestration'
  },
  {
    id: 'AgentMemoryManager',
    name: 'Agent Memory Manager',
    capabilities: ['context_management', 'state_persistence', 'memory_retrieval'],
    category: 'orchestration'
  }
];

// Real workflow: deploy-feature
const DEPLOY_FEATURE_EDGES: AgentEdge[] = [
  {
    from: 'SecurityReviewerAgent',
    to: 'AuditorAgent',
    type: 'triggers',
    label: 'validates',
    weight: 1
  },
  {
    from: 'AuditorAgent',
    to: 'TestAgent',
    type: 'triggers',
    label: 'tests',
    weight: 1
  },
  {
    from: 'TestAgent',
    to: 'DeploymentAgent',
    type: 'triggers',
    label: 'deploys',
    weight: 1
  },
  {
    from: 'DeploymentAgent',
    to: 'DatabaseAgent',
    type: 'depends_on',
    label: 'requires',
    weight: 1
  }
];

/**
 * Get deploy-feature workflow topology
 */
export function getDeployFeatureTopology(): Topology {
  return {
    namespace: 'deploy-feature-workflow',
    version: '1.0.0',
    type: 'workflow',
    nodes: REAL_AGENTS.filter(agent =>
      DEPLOY_FEATURE_EDGES.some(edge => edge.from === agent.id || edge.to === agent.id)
    ),
    edges: DEPLOY_FEATURE_EDGES
  };
}

/**
 * Get all agents topology
 */
export function getAllAgentsTopology(): Topology {
  // Create connections based on typical agent interactions
  const edges: AgentEdge[] = [
    // Classification → Routing
    {
      from: 'AgentClassifier',
      to: 'AgentMemoryManager',
      type: 'triggers',
      label: 'stores context',
      weight: 1
    },
    // Security workflow
    {
      from: 'SecurityReviewerAgent',
      to: 'AuditorAgent',
      type: 'triggers',
      label: 'validates',
      weight: 1
    },
    // Analysis → Knowledge
    {
      from: 'PatternHarvestingAgent',
      to: 'AgentMemoryManager',
      type: 'triggers',
      label: 'stores patterns',
      weight: 1
    },
    // Testing → Deployment
    {
      from: 'TestAgent',
      to: 'DeploymentAgent',
      type: 'triggers',
      label: 'validates for deploy',
      weight: 1
    },
    // Deployment dependencies
    {
      from: 'DeploymentAgent',
      to: 'DatabaseAgent',
      type: 'depends_on',
      label: 'requires',
      weight: 1
    }
  ];

  return {
    namespace: 'all-agents',
    version: '1.0.0',
    type: 'workflow',
    nodes: REAL_AGENTS,
    edges
  };
}

/**
 * Get agent by category
 */
export function getAgentsByCategory(category: string): AgentNode[] {
  return REAL_AGENTS.filter(agent => agent.category === category);
}

/**
 * Get all categories
 */
export function getCategories(): string[] {
  return [...new Set(REAL_AGENTS.map(agent => agent.category))];
}
